import React, { useState, useEffect } from 'react';
import HybridCreditService from '../services/hybridCreditService';
import Icon from './AppIcon';
import Button from './ui/Button';

const CreditManagement = ({ userId, isPremium }) => {
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const fetchData = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const [balanceResult, historyResult] = await Promise.all([
        HybridCreditService.getCreditBreakdown(userId),
        HybridCreditService.getCreditHistory(userId, 20)
      ]);

      if (balanceResult.success) {
        setBalance(balanceResult.data);
      } else {
        setError(balanceResult.error);
      }

      if (historyResult.success) {
        setHistory(historyResult.data);
      }
    } catch (err) {
      console.error('Error fetching credit data:', err);
      setError('Failed to load credit information.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatExpiryDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const getReasonIcon = (reason) => {
    switch (reason) {
      case 'monthly_grant': return 'Calendar';
      case 'purchase': return 'CreditCard';
      case 'assessment_use': return 'ClipboardList';
      case 'expiry': return 'Clock';
      default: return 'Coins';
    }
  };

  const getReasonColor = (reason) => {
    switch (reason) {
      case 'monthly_grant': return 'text-primary';
      case 'purchase': return 'text-success';
      case 'assessment_use': return 'text-warning';
      case 'expiry': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-4 bg-muted rounded w-1/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 text-center">
        <Icon name="AlertTriangle" size={24} className="text-error mx-auto mb-2" />
        <p className="text-error">{error}</p>
        <Button onClick={fetchData} variant="outline" size="sm" className="mt-2">
          Try Again
        </Button>
      </div>
    );
  }

  if (!balance) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 text-center">
        <Icon name="Coins" size={24} className="text-muted-foreground mx-auto mb-2" />
        <p className="text-muted-foreground">No credit information available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Credit Overview */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Credit Balance</h3>
          <div className="flex items-center space-x-2">
            <Icon name="Coins" size={20} className="text-primary" />
            <span className="text-2xl font-bold text-primary">
              {balance.totalAvailable}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Monthly Credits */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Monthly Credits</span>
              <span className="text-sm text-muted-foreground">
                {balance.snapshot.monthly_credits_available} of 3 left
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${(balance.snapshot.monthly_credits_available / 3) * 100}%` }}
              ></div>
            </div>
            <p className="text-xs text-muted-foreground">
              Reset at month-end
            </p>
          </div>

          {/* Purchased Credits */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Purchased Credits</span>
              <span className="text-sm text-muted-foreground">
                {balance.snapshot.purchased_credits_available} available
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-success h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((balance.snapshot.purchased_credits_available / 10) * 100, 100)}%` }}
              ></div>
            </div>
            <p className="text-xs text-muted-foreground">
              Expire 3 months after purchase
            </p>
          </div>
        </div>

        {balance.soonestExpiry && (
          <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} className="text-warning" />
              <span className="text-sm text-warning font-medium">Soonest expiry:</span>
              <span className="text-sm text-warning">
                {formatExpiryDate(balance.soonestExpiry)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Credit History */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Credit History</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? 'Hide' : 'Show'} History
          </Button>
        </div>

        {showHistory && (
          <div className="space-y-3">
            {history.length === 0 ? (
              <p className="text-muted-foreground text-center py-1">No credit history yet</p>
            ) : (
              history.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between py-2 border-b border-border last:border-b-0">
                  <div className="flex items-center space-x-3">
                    <Icon 
                      name={getReasonIcon(entry.reason)} 
                      size={16} 
                      className={getReasonColor(entry.reason)}
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {entry.reason === 'monthly_grant' && 'Monthly Grant'}
                        {entry.reason === 'purchase' && 'Credit Purchase'}
                        {entry.reason === 'assessment_use' && 'Assessment Used'}
                        {entry.reason === 'expiry' && 'Credits Expired'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(entry.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-sm font-medium ${
                      entry.delta > 0 ? 'text-success' : 'text-error'
                    }`}>
                      {entry.delta > 0 ? '+' : ''}{entry.delta}
                    </span>
                    {entry.lot && (
                      <p className="text-xs text-muted-foreground">
                        Expires {formatExpiryDate(entry.lot.expires_at)}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditManagement;


